import React, { memo, useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { fmtValue } from "./format";
import type { Series } from "./types";

interface Props {
    seriesList: Series[];
    data: Record<string, unknown>[];
    setIndexRef: React.MutableRefObject<(i: number) => void>;
    digits?: number;
    indexKey?: string; // デフォルト: "index"
}

export const HoverLegend = memo(function HoverLegend({
    seriesList,
    data,
    setIndexRef,
    digits = 3,
    indexKey = "index",
}: Props) {
    const [i, setI] = useState(0);

    // 親から呼べる setter を登録（親は再レンダ不要）
    useEffect(() => {
        setIndexRef.current = (n: number) => setI((prev) => (prev === n ? prev : n));
    }, [setIndexRef]);

    const row = data[i] ?? {};
    const indexLabel = (row[indexKey] as number | string | undefined) ?? "-";

    // data-* 属性は PNG ダウンロード（exportChartPng）が凡例を画像へ合成するための抽出口。
    // HTML である凡例は svg 直列化に乗らないため、ここで色・ラベル・値を機械可読に出す。
    return (
        <Box sx={{ ml: 1, mt: 1 }} data-chart-legend data-legend-index={String(indexLabel)}>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                Index: {indexLabel}
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.5}>
                {seriesList.map((s) => {
                    const value = String(fmtValue((row as any)[s.key], digits));
                    return (
                        <Stack
                            key={s.key}
                            direction="row"
                            alignItems="center"
                            gap={0.75}
                            data-legend-item
                            data-legend-color={s.color}
                            data-legend-label={s.name}
                            data-legend-value={value}
                        >
                            <Box sx={{ width: 10, height: 10, bgcolor: s.color, borderRadius: 0.5 }} />
                            <Typography variant="caption">{`${s.name}: ${value}`}</Typography>
                        </Stack>
                    );
                })}
            </Stack>
        </Box>
    );
});
